import React, { useState } from 'react';
import { Truck, Car, Info, MapPin, ShieldCheck, ArrowRight, ClipboardList, Fuel, Phone, User } from 'lucide-react';

const VehicleTransportForm = () => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '', phone: '', vehicleType: 'Car',
    model: '', condition: 'Running', fuel: 'Low',
    pickup: '', drop: '', transportType: 'Open',
    insurance: false
  });

  const handleSubmit = async () => {
    setLoading(true);
    const finalData = { 
      ...formData, 
      serviceType: "Vehicle Logistics", 
      createdAt: new Date().toISOString() 
    };
    
    try {
      // n8n ला डेटा पाठवा (Production URL अपडेट केली आहे)
      await fetch("https://copper-focus-leading-thereby.trycloudflare.com/webhook/packer-movers", {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(finalData)
      });
      alert("तुमची माहिती यशस्वीरित्या पाठवली आहे! ✅");
    } catch (err) {
      console.error(err);
      alert("काहीतरी तांत्रिक अडचण आली.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 pb-40">
      {/* --- Header --- */}
      <div className="bg-[#002D5E] text-white p-6 sticky top-[72px] lg:top-[108px] z-30 shadow-lg">
        <h1 className="text-xl font-black italic tracking-tighter">APNI MANZIL <span className="text-orange-400">VEHICLE LOGISTICS</span></h1>
        <p className="text-[10px] uppercase font-bold tracking-widest opacity-70">Safe Car & Bike Relocation</p>
      </div>

      <div className="max-w-4xl mx-auto p-4 md:p-8">
        
        {/* --- 0. Customer Contact Info ( नाव आणि +91 मोबाईल नंबर ) --- */}
        <div className="mb-8 bg-white rounded-[2.5rem] p-6 md:p-8 shadow-sm border border-slate-100">
          <h2 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-6 flex items-center gap-2">
            <User size={16} className="text-blue-600" /> Customer Details
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label className="text-[10px] font-black text-slate-400 uppercase ml-2">Full Name</label>
              <div className="relative">
                <User className="absolute left-4 top-4 text-slate-400" size={18} />
                <input 
                  type="text" 
                  placeholder="Enter your full name" 
                  className="w-full pl-12 p-4 bg-slate-50 rounded-2xl border-none outline-none font-bold focus:ring-2 ring-orange-500 text-sm"
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-[10px] font-black text-slate-400 uppercase ml-2">Mobile Number</label>
              <div className="flex items-center bg-slate-50 rounded-2xl overflow-hidden focus-within:ring-2 ring-orange-500">
                <span className="pl-4 pr-2 text-slate-500 font-bold select-none bg-slate-50 text-sm">
                  +91
                </span>
                <input 
                  type="tel" 
                  maxLength="10" 
                  placeholder="Enter mobile number" 
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, '').slice(0, 10);
                    setFormData({...formData, phone: val});
                  }} 
                  className="w-full p-4 bg-slate-50 border-none outline-none text-sm font-bold" 
                />
              </div>
            </div>
          </div>
        </div>

        {/* --- 1. Vehicle Selection & Basic Info --- */}
        <div className="mb-8 bg-white rounded-[2.5rem] p-6 md:p-8 shadow-sm border border-slate-100">
          <h2 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-6 flex items-center gap-2">
            <Car size={16} className="text-orange-500" /> Vehicle Information
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label className="text-[10px] font-black text-slate-400 uppercase ml-2">Select Vehicle Type</label>
              <select 
                className="w-full p-4 bg-slate-50 rounded-2xl border-none font-bold text-slate-700 focus:ring-2 ring-orange-500"
                onChange={(e) => setFormData({...formData, vehicleType: e.target.value})}
              >
                <option value="Car">Car 🚗</option>
                <option value="Bike">Bike 🏍️</option>
                <option value="Scooter">Scooter / Activa</option>
                <option value="Bicycle">Bicycle 🚲</option>
                <option value="Commercial">Commercial Vehicle 🚛</option>
              </select>
            </div>

            <div className="space-y-2">
              <label className="text-[10px] font-black text-slate-400 uppercase ml-2">Brand / Model Name</label>
              <div className="relative">
                <ClipboardList className="absolute left-4 top-4 text-slate-400" size={18} />
                <input 
                  type="text" placeholder="e.g. Swift, Honda Activa, KTM" 
                  className="w-full pl-12 p-4 bg-slate-50 rounded-2xl border-none outline-none font-bold focus:ring-2 ring-orange-500"
                  onChange={(e) => setFormData({...formData, model: e.target.value})}
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-[10px] font-black text-slate-400 uppercase ml-2">Vehicle Condition</label>
              <div className="flex gap-3">
                {['Running', 'Non-running'].map(cond => (
                  <button 
                    key={cond}
                    onClick={() => setFormData({...formData, condition: cond})}
                    className={`flex-1 py-3 rounded-xl font-bold text-sm transition-all ${formData.condition === cond ? 'bg-orange-500 text-white shadow-lg shadow-orange-200' : 'bg-slate-100 text-slate-500'}`}
                  >
                    {cond}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-[10px] font-black text-slate-400 uppercase ml-2 flex items-center gap-1">
                <Fuel size={12}/> Fuel Level
              </label>
              <select 
                className="w-full p-4 bg-slate-50 rounded-2xl border-none font-bold text-slate-700 focus:ring-2 ring-orange-500"
                onChange={(e) => setFormData({...formData, fuel: e.target.value})}
              >
                <option value="Empty">Empty (Best for safety)</option>
                <option value="Low">Low</option>
                <option value="Full">Full</option>
              </select>
            </div>
          </div>
        </div>

        {/* --- 2. Location Details --- */}
        <div className="mb-8 bg-white rounded-[2.5rem] p-6 md:p-8 shadow-sm border border-slate-100">
          <h2 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-6 flex items-center gap-2">
            <MapPin size={16} className="text-red-500" /> Pickup & Drop Details
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="relative">
              <MapPin className="absolute left-4 top-4 text-red-500" size={18} />
              <textarea placeholder="Pickup Location Address" className="w-full pl-12 p-4 bg-slate-50 rounded-2xl border-none outline-none focus:ring-2 ring-blue-500 font-bold text-sm" rows="3" onChange={(e) => setFormData({...formData, pickup: e.target.value})}></textarea>
            </div>
            <div className="relative">
              <MapPin className="absolute left-4 top-4 text-green-500" size={18} />
              <textarea placeholder="Drop Location Address" className="w-full pl-12 p-4 bg-slate-50 rounded-2xl border-none outline-none focus:ring-2 ring-blue-500 font-bold text-sm" rows="3" onChange={(e) => setFormData({...formData, drop: e.target.value})}></textarea>
            </div>
          </div>
        </div>

        {/* --- 3. Transport Type & Safety --- */}
        <div className="mb-8 bg-white rounded-[2.5rem] p-6 md:p-8 shadow-sm border border-orange-50">
          <h2 className="text-xs font-black text-orange-600 uppercase tracking-widest mb-6 flex items-center gap-2">
            <Truck size={16} /> Transport Type & Safety
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <label className={`flex items-center justify-between p-4 rounded-2xl border-2 cursor-pointer transition-all ${formData.transportType === 'Open' ? 'border-orange-500 bg-orange-50' : 'border-slate-100'}`}
                onClick={() => setFormData({...formData, transportType: 'Open'})}>
                <div>
                   <p className="font-bold text-slate-800">Open Transport</p>
                   <p className="text-[10px] text-slate-500 font-bold uppercase tracking-tighter">Budget Friendly</p>
                </div>
              </label>
              <label className={`flex items-center justify-between p-4 rounded-2xl border-2 cursor-pointer transition-all ${formData.transportType === 'Enclosed' ? 'border-orange-500 bg-orange-50' : 'border-slate-100'}`}
                onClick={() => setFormData({...formData, transportType: 'Enclosed'})}>
                <div>
                   <p className="font-bold text-slate-800">Enclosed Transport</p>
                   <p className="text-[10px] text-orange-600 font-bold uppercase tracking-tighter italic">Premium Safety</p>
                </div>
              </label>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-2xl">
                <span className="text-sm font-bold text-slate-700 flex items-center gap-2"><ShieldCheck size={16} className="text-green-600"/> Transit Insurance</span>
                <input type="checkbox" className="w-5 h-5 accent-orange-600" onChange={(e) => setFormData({...formData, insurance: e.target.checked})} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* --- Footer Summary --- */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t p-3 px-4 pb-[max(0.75rem,env(safe-area-inset-bottom))] flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:p-6 sm:px-10 shadow-2xl z-[100]">
        <div className="flex flex-col">
          <span className="text-[10px] font-black text-slate-400 uppercase tracking-tighter">Transport Mode</span>
          <span className="text-sm sm:text-xl font-black text-[#002D5E]">{formData.transportType} <span className="text-orange-500">Carrier</span></span>
        </div>
        <button onClick={handleSubmit} disabled={loading} className="w-full sm:w-auto bg-orange-500 hover:bg-orange-600 text-white px-4 sm:px-10 py-3 sm:py-4 rounded-2xl font-black uppercase text-xs sm:text-sm tracking-wide sm:tracking-widest flex items-center justify-center gap-3 shadow-xl transition-all active:scale-95">
          {loading ? "Processing..." : "Get Vehicle Quote"} <ArrowRight size={18} />
        </button>
      </div>
    </div>
  );
};

export default VehicleTransportForm;